// redux/slices/timeActivitiesSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';

interface TimeActivitiesFilters {
	currentPage: number;
	pageSize: number;
	sortField: string;
	sortOrder: string;
	date: [string, string];
	statusFilterValue: string;
	currentPayPeriodIndex: number;
	staffCode: string;
}

interface TimeActivitiesData {
	timeActivities: any[];
	totalRecords: number;
	payPeriods: any[];
}

interface TimeActivitiesState {
	filters: TimeActivitiesFilters;
	data: TimeActivitiesData;
	loading: {
		timeActivities: boolean;
		payPeriods: boolean;
	};
	initialized: boolean;
}

const initialState: TimeActivitiesState = {
	filters: {
		currentPage: 1,
		pageSize: 30,
		sortField: '',
		sortOrder: '',
		date: [dayjs().startOf('month').toISOString(), dayjs().toISOString()],
		statusFilterValue: '',
		currentPayPeriodIndex: 0,
		staffCode: 'HOURLY',
	},
	data: {
		timeActivities: [],
		totalRecords: 0,
		payPeriods: [],
	},
	loading: {
		timeActivities: false,
		payPeriods: false,
	},
	initialized: false,
};

const monthlySummarySlice = createSlice({
	name: 'timeActivities',
	initialState,
	reducers: {
		setFilters: (
			state,
			action: PayloadAction<Partial<TimeActivitiesFilters>>
		) => {
			state.filters = { ...state.filters, ...action.payload };
		},

		setTimeActivities: (
			state,
			action: PayloadAction<{
				timeActivities: any[];
				totalRecords: number;
			}>
		) => {
			state.data.timeActivities = action.payload.timeActivities;
			state.data.totalRecords = action.payload.totalRecords;
		},

		setPayPeriods: (state, action: PayloadAction<any[]>) => {
			state.data.payPeriods = action.payload;
		},

		setTimeActivitiesLoading: (state, action: PayloadAction<boolean>) => {
			state.loading.timeActivities = action.payload;
		},

		setPayPeriodsLoading: (state, action: PayloadAction<boolean>) => {
			state.loading.payPeriods = action.payload;
		},

		setInitialized: (state, action: PayloadAction<boolean>) => {
			state.initialized = action.payload;
		},

		resetFilters: (state) => {
			state.filters = initialState.filters;
			state.initialized = false;
		},

		// Action to sync pay period index with current date
		syncPayPeriodIndex: (
			state,
			action: PayloadAction<{
				payPeriods: any[];
				currentDate: [string, string];
			}>
		) => {
			const { payPeriods, currentDate } = action.payload;

			if (payPeriods.length > 0) {
				const savedStartDate = dayjs(currentDate[0]).format(
					'YYYY-MM-DD'
				);
				const savedEndDate = dayjs(currentDate[1]).format('YYYY-MM-DD');

				const matchingPeriodIndex = payPeriods.findIndex(
					(period: any) =>
						dayjs(period.startDate).format('YYYY-MM-DD') ===
							savedStartDate &&
						dayjs(period.endDate).format('YYYY-MM-DD') ===
							savedEndDate
				);

				if (matchingPeriodIndex !== -1) {
					state.filters.currentPayPeriodIndex = matchingPeriodIndex;
				} else {
					state.filters.currentPayPeriodIndex = 0;
				}
			}
		},

		// Action to initialize with current pay period
		initializeWithCurrentPayPeriod: (
			state,
			action: PayloadAction<any[]>
		) => {
			const payPeriods = action.payload;

			if (payPeriods.length > 0) {
				const today = dayjs();
				const currentPeriodIndex = payPeriods.findIndex(
					(period: any) =>
						today.isSameOrAfter(dayjs(period.startDate)) &&
						today.isSameOrBefore(dayjs(period.endDate))
				);

				if (currentPeriodIndex !== -1) {
					state.filters.currentPayPeriodIndex = currentPeriodIndex;
					state.filters.date = [
						payPeriods[currentPeriodIndex].startDate,
						payPeriods[currentPeriodIndex].endDate,
					];
				} else {
					state.filters.currentPayPeriodIndex = 0;
					state.filters.date = [
						payPeriods[0].startDate,
						payPeriods[0].endDate,
					];
				}
			}
		},

		// Handle table pagination changes
		updatePagination: (
			state,
			action: PayloadAction<{
				currentPage: number;
				pageSize: number;
				totalRecords: number;
			}>
		) => {
			const { currentPage, pageSize, totalRecords } = action.payload;
			state.filters.currentPage = currentPage;
			state.filters.pageSize = pageSize;
			state.data.totalRecords = totalRecords;
		},

		// Handle table sorting changes
		updateSorting: (
			state,
			action: PayloadAction<{ sortField: string; sortOrder: string }>
		) => {
			const { sortField, sortOrder } = action.payload;
			state.filters.sortField = sortField;
			state.filters.sortOrder = sortOrder;
		},
	},
});

export const {
	setFilters,
	setTimeActivities,
	setPayPeriods,
	setTimeActivitiesLoading,
	setPayPeriodsLoading,
	setInitialized,
	resetFilters,
	syncPayPeriodIndex,
	initializeWithCurrentPayPeriod,
	updatePagination,
	updateSorting,
} = monthlySummarySlice.actions;

export default monthlySummarySlice;
